import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { identifyTikTokUser } from '@/lib/tiktokTracking';
import { identifyXUser } from '@/lib/xAdsTracking';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: any | null;
  loading: boolean;
  isAdmin: boolean;
  /** True until the admin-role RPC resolves. Guards should wait on this
   *  instead of just `loading` — loading flips false before the role check
   *  completes, which briefly reports isAdmin=false for real admins. */
  adminRoleLoading: boolean;
  signUp: (email: string, password: string, userData: any) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRoleLoading, setAdminRoleLoading] = useState(true);
  const { toast } = useToast();

  // Refs to avoid stale closure inside onAuthStateChange (which is registered once)
  const userRef = useRef<User | null>(null);
  const profileRef = useRef<any | null>(null);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  // ⚠️ DEPRECATED — DO NOT CALL THIS FOR UNCONFIRMED USERS.
  //
  // The `send-signup-otp` edge function uses admin.generateLink({
  // type: 'magiclink' }), which writes `recovery_sent_at` on the user
  // record and INVALIDATES the freshly-issued signup OTP. Calling this
  // immediately after `supabase.auth.signUp` strands the user with
  // "Token has expired or is invalid" on the verify screen. This was the
  // root cause of the May 11 stranded-creators incident (~18 users) and
  // we removed every caller. The function itself now guards against
  // unconfirmed users server-side and returns 409.
  //
  // Safe path for resending a signup OTP:
  //   supabase.auth.resend({ type: 'signup', email })
  //
  // This helper is retained only for potential passwordless-sign-in
  // flows for ALREADY-CONFIRMED users (no current callers).
  const sendSignupOtp = async (email: string): Promise<void> => {
    const { data, error } = await supabase.functions.invoke('send-signup-otp', {
      body: { email },
    });
    if (error) {
      console.error('send-signup-otp invoke error', error);
      const ctx: any = (error as any)?.context;
      let detail = error.message || 'Failed to send verification code';
      try {
        if (ctx && typeof ctx.json === 'function') {
          const body = await ctx.json();
          if (body?.error) detail = body.error;
        }
      } catch {
        // ignore body parse failures
      }
      throw new Error(detail);
    }
    if (!data || (data as any).ok !== true) {
      const detail = (data as any)?.error || 'Failed to send verification code';
      console.error('send-signup-otp non-ok response', data);
      throw new Error(detail);
    }
  };

  useEffect(() => {
    // 🛡️ SAFETY NET: If auth bootstrap doesn't resolve within 8s, force-unblock
    // the UI. Otherwise users land on the loading spinner forever when:
    //   - profile fetch hangs (RLS misconfig, network stall, Supabase outage)
    //   - onAuthStateChange never fires for an existing session
    //   - has_role RPC stalls (it's awaited inside the listener)
    // This mirrors the same 5s timeout we already have inside signIn/signUp
    // but applied to the INITIAL page load — which was the missing piece
    // that caused the "frozen the whole time and bugs out like crazy" reports.
    const bootSafetyTimer = setTimeout(() => {
      setLoading((current) => {
        if (current) {
          console.warn('[useAuth] Bootstrap safety timeout fired — forcing loading=false');
        }
        return false;
      });
    }, 8000);

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        
        if (event === 'SIGNED_OUT' || !session) {
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);

        // Attach the user's hashed identity to the TikTok pixel so all
        // subsequent pixel events carry email + external_id for EMQ. Safe to
        // call repeatedly (idempotent). No-ops if window.ttq is not loaded.
        if (session?.user) {
          try {
            identifyTikTokUser({
              email: session.user.email ?? undefined,
              externalId: session.user.id,
            });
            identifyXUser({
              email: session.user.email ?? undefined,
              externalId: session.user.id,
            });
          } catch { /* non-fatal */ }
        }
        
        if (session?.user) {
          // Skip redundant profile re-fetches on token refresh.
          // Read from refs so this listener (registered once) is not bound to stale state.
          const isSameUser = userRef.current?.id === session.user.id;
          if (isSameUser && profileRef.current) {
            setLoading(false);
            return;
          }
          
          // Fetch profile + admin role in parallel; do not block loading on the role check.
          (async () => {
            try {
              const profilePromise = supabase
                .from('profiles')
                .select('*')
                .eq('user_id', session.user.id)
                .maybeSingle();
              const adminPromise = supabase.rpc('has_role', {
                _user_id: session.user.id,
                _role: 'admin' as any,
              });

              const { data: profileData, error } = await profilePromise;
              
              if (error && error.code !== 'PGRST116') {
                console.error('Error fetching profile:', error);
              } else if (!profileData) {
                // SAFETY NET: Profile is missing - attempt to create it
                console.warn('Profile missing for user, attempting to create...');
                
                // Get user metadata from session
                const metadata = session.user.user_metadata || {};
                const role = metadata.role || metadata.userType || 'creator';
                
                try {
                  const { data: newProfile, error: createError } = await supabase
                    .from('profiles')
                    .insert({
                      user_id: session.user.id,
                      role: role === 'creator' ? 'creator' : role,
                      first_name: metadata.firstName || metadata.first_name,
                      last_name: metadata.lastName || metadata.last_name,
                    })
                    .select()
                    .single();
                  
                  if (createError) {
                    console.error('Failed to create missing profile:', createError);
                    toast({
                      title: "Profile Setup Required",
                      description: "Please contact support to complete your account setup.",
                      variant: "destructive",
                    });
                  } else {
                    setProfile(newProfile);
                    toast({
                      title: "Account Restored",
                      description: "Your profile has been restored successfully.",
                    });
                  }
                } catch (createErr) {
                  console.error('Error creating profile:', createErr);
                }
              } else {
                setProfile(profileData);
              }

              // Unblock UI immediately; resolve admin role in the background.
              setLoading(false);

              try {
                const { data: hasAdminRole, error: roleError } = await adminPromise;
                setIsAdmin(hasAdminRole === true);
              } catch (roleErr) {
                console.error('Admin role check failed:', roleErr);
              } finally {
                setAdminRoleLoading(false);
              }
            } catch (err) {
              console.error('Error in profile fetch:', err);
              setLoading(false);
            }
          })();
        } else {
          setProfile(null);
          setIsAdmin(false);
          setAdminRoleLoading(false);
          setLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Don't set state here - let onAuthStateChange handle it
      // This prevents race conditions
      if (!session) {
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(bootSafetyTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, userData: any) => {
    try {
      setLoading(true);
      
      // 🔵 DEBUG: Log userData received
      
      const metadataToSend = {
        role: userData.userType === 'creator' ? 'creator' : userData.userType,
        firstName: userData.firstName,
        lastName: userData.lastName,
        companyName: userData.companyName
      };
      
      // 🟢 DEBUG: Log metadata being sent to Supabase
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadataToSend
        }
      });
      
      // 🟡 DEBUG: Log Supabase response

      if (error) {
        let errorMessage = error.message;
        let errorTitle = "Error signing up";

        // Handle specific error cases.
        //
        // NOTE: We DO NOT fall back to sendSignupOtp on `unexpected_failure` /
        // "error sending confirmation email" anymore. That fallback was the
        // root cause of the May 11 stranded-creators incident: Supabase
        // sometimes returns `unexpected_failure` on SMTP soft-fails (e.g.
        // typo'd / non-existent domains) AFTER the built-in mailer already
        // committed a signup token. Calling our edge function
        // `send-signup-otp` then re-issues a magiclink via admin.generateLink
        // which sets `recovery_sent_at` and INVALIDATES the original signup
        // OTP — every affected user got "Token has expired or is invalid".
        // If the built-in mailer truly couldn't send, the user can hit the
        // explicit Resend button which now uses supabase.auth.resend({
        // type: 'signup' }) — the safe, token-preserving path.
        if (error.message.includes('already registered') || error.message.includes('User already registered')) {
          errorTitle = "Account Already Exists";
          errorMessage = "This email is already registered. Try signing in instead, or use 'Forgot Password' if you need to reset your password.";
        } else if (error.message.includes('rate limit') || error.message.includes('429')) {
          errorTitle = "Too Many Attempts";
          errorMessage = "Please wait a moment before trying again. Check your email - the verification link may already be there.";
        } else if (
          (error as any)?.code === 'unexpected_failure' ||
          error.message?.toLowerCase().includes('error sending confirmation email') ||
          error.message?.toLowerCase().includes('error sending')
        ) {
          errorTitle = "Couldn't send verification email";
          errorMessage = "We couldn't deliver a verification code to that address. Double-check the email is correct, or try again in a moment.";
        }

        toast({
          title: errorTitle,
          description: errorMessage,
          variant: "destructive",
        });
        setLoading(false);
        return { error };
      }

        // 🛡️  Email-enumeration protection: when Supabase has
        // "Prevent email enumeration" enabled (recommended), signing up with
        // an email that's ALREADY confirmed returns success-shaped data but
        // with `user.identities` as an empty array — and NO email is sent.
        // Without this check we route the user to the verify screen for a
        // code that will never arrive. Detect and surface a clear error.
        if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
          toast({
            title: "Account Already Exists",
            description: "This email is already registered. Try signing in instead, or use 'Forgot Password' if you need to reset your password.",
            variant: "destructive",
          });
          setLoading(false);
          return { error: { message: 'User already registered', name: 'AuthApiError', code: 'user_already_exists' } as any };
        }

        // Check if email confirmation is required
        if (data.user && !data.session) {
          // DO NOT generate a second OTP here. supabase.auth.signUp() above
          // already triggered Supabase's built-in confirmation mailer (routed
          // through Resend SMTP), which delivered a styled {{ .Token }} email
          // with the signup OTP. Calling sendSignupOtp() here would invoke
          // admin.generateLink({ type: 'magiclink' }) which INVALIDATES the
          // freshly issued signup token — every new creator would then type
          // the code from email #1 and get "Token has expired or is invalid",
          // exactly the failure mode we saw on May 11 (~18 users stranded
          // unconfirmed in a 4-hour window). Forensic signature in auth.users:
          // `recovery_sent_at` set 2–3s after `confirmation_sent_at`.
          //
          // sendSignupOtp() is still wired into the explicit "Resend code"
          // button — user-initiated resends are safe because by then the
          // original token is already stale or about to be replaced anyway.
          toast({
            title: "Check Your Email",
            description: "We've sent a 6-digit verification code to your inbox.",
          });

          // Store email for resend functionality
          localStorage.setItem('pendingVerificationEmail', email);

          return { error: null };
        }

        if (data.user && data.session) {
          // Poll for the profile created by the database trigger — up to ~3s.
          // Avoids a hard 2s wait when the trigger has already finished.
          let profileCheck: any = null;
          let profileCheckError: any = null;
          for (let attempt = 0; attempt < 10; attempt++) {
            const { data: row, error: pErr } = await supabase
              .from('profiles')
              .select('id, role, first_name, last_name, organization_id')
              .eq('user_id', data.user.id)
              .maybeSingle();
            profileCheck = row;
            profileCheckError = pErr;
            if (row || (pErr && pErr.code !== 'PGRST116')) break;
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
          
          // 🔍 DEBUG: Log profile check result
          
          if (profileCheckError) {
            console.error('❌ Error checking profile:', profileCheckError);
          }
          
          // Validate the profile matches expected data
          const expectedRole = userData.userType === 'creator' ? 'creator' : userData.userType;
          
          if (profileCheck) {
            // Check if profile matches expectations
            const hasRoleMismatch = profileCheck.role !== expectedRole;
            const hasMissingName = !profileCheck.first_name || !profileCheck.last_name;
            const needsOrganization = userData.userType === 'business' && !profileCheck.organization_id;
            
            if (hasRoleMismatch || hasMissingName || needsOrganization) {
              console.warn('⚠️ Profile mismatch detected, attempting to fix:', {
                expected: { role: expectedRole, firstName: userData.firstName, lastName: userData.lastName },
                actual: profileCheck,
                fixes: { hasRoleMismatch, hasMissingName, needsOrganization }
              });
              
              // Prepare update data
              const updateData: any = {};
              if (hasRoleMismatch) updateData.role = expectedRole;
              if (hasMissingName) {
                updateData.first_name = userData.firstName;
                updateData.last_name = userData.lastName;
              }
              
              // If business user needs an organization
              if (needsOrganization) {
                const orgName = userData.companyName || `${userData.firstName}'s Organization`;
                const timestamp = Date.now();
                const baseSlug = orgName.toLowerCase().replace(/[^a-z0-9]/g, '-');
                const uniqueSlug = `${baseSlug}-${timestamp}`;
                
                const { data: orgData, error: orgError } = await supabase
                  .from('organizations')
                  .insert({
                    name: orgName,
                    description: `Organization for ${userData.firstName} ${userData.lastName}`,
                    slug: uniqueSlug
                  })
                  .select()
                  .single();
                
                if (orgData) {
                  updateData.organization_id = orgData.id;
                } else {
                  console.error('❌ Failed to create organization:', orgError);
                }
              }
              
              // Update profile
              const { error: fixError } = await supabase
                .from('profiles')
                .update(updateData)
                .eq('user_id', data.user.id);
              
              if (fixError) {
                console.error('❌ Failed to fix profile:', fixError);
              } else {
                
                // Fix welcome notifications
                
                // Delete incorrect notifications
                await supabase
                  .from('user_notifications')
                  .delete()
                  .eq('profile_id', profileCheck.id)
                  .in('type', ['creator_welcome', 'business_welcome']);
                
                // Send correct notification
                const notificationType = expectedRole === 'business' ? 'business_welcome' : 'creator_welcome';
                const notificationMessage = expectedRole === 'business' 
                  ? `Welcome ${userData.firstName}! Start building your creator community.`
                  : `Welcome ${userData.firstName}! Connect your accounts and start earning.`;
                
                await supabase
                  .from('user_notifications')
                  .insert({
                    profile_id: profileCheck.id,
                    type: notificationType,
                    title: 'Welcome to CreatorVault!',
                    message: notificationMessage,
                    important: true,
                    metadata: {
                      first_name: userData.firstName,
                      last_name: userData.lastName,
                      company_name: userData.companyName,
                      role: expectedRole
                    }
                  });
                
              }
            }
            
            // Refresh profile data
            const { data: updatedProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('user_id', data.user.id)
              .single();
            
            if (updatedProfile) {
              setProfile(updatedProfile);
            }
          }

          toast({
            title: "Account created successfully!",
            description: "Welcome to CreatorVault",
          });
          
          // Safety timeout for signUp: if onAuthStateChange doesn't resolve loading within 5s, force it
          setTimeout(() => {
            setLoading((current) => {
              if (current) {
                console.warn('signUp safety timeout: forcing loading to false');
                return false;
              }
              return current;
            });
          }, 5000);
        }

      return { error: null };
    } catch (err) {
      const error = err as Error;
      setLoading(false);
      toast({
        title: "Error signing up",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setLoading(false);
        toast({
          title: "Error signing in",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      toast({
        title: "Welcome back!",
        description: "You've signed in successfully",
      });

      // Safety timeout: if onAuthStateChange doesn't resolve loading within 5s, force it
      setTimeout(() => {
        setLoading((current) => {
          if (current) {
            console.warn('signIn safety timeout: forcing loading to false');
            return false;
          }
          return current;
        });
      }, 5000);

      return { error: null };
    } catch (err) {
      const error = err as Error;
      setLoading(false);
      toast({
        title: "Error signing in",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      
      // Clear state immediately for faster UI response
      setUser(null);
      setSession(null);
      setProfile(null);
      setIsAdmin(false);
      setAdminRoleLoading(false);
      setLoading(false);
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error);
        toast({
          title: "Error signing out",
          description: error.message,
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error('Unexpected sign out error:', err);
      // Still clear state even if there's an error
      setUser(null);
      setSession(null);
      setProfile(null);
      setIsAdmin(false);
      setAdminRoleLoading(false);
      setLoading(false);
    }
  };

  const refreshProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (profileData) {
      setProfile(profileData);
    }
  };

  const resendVerificationEmail = async (email: string) => {
    try {
      // 🛡️  Use Supabase's native signup-resend instead of our
      // send-signup-otp edge function. The edge function calls
      // admin.generateLink({ type: 'magiclink' }) which writes
      // `recovery_sent_at` and INVALIDATES the original signup token.
      // supabase.auth.resend({ type: 'signup' }) re-sends the same
      // signup OTP without invalidating tokens — the only safe path
      // for an unconfirmed-user verification resend.
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      if (error) {
        // Fall back to the edge function ONLY if the native resend rejects
        // — e.g. because the user is somehow not in an unconfirmed state.
        // The edge function will guard against firing for confirmed users.
        if (error.message?.toLowerCase().includes('already confirmed') ||
            (error as any)?.code === 'user_already_confirmed') {
          toast({
            title: "Account Already Verified",
            description: "This email is already confirmed. Try signing in instead.",
            variant: "destructive",
          });
          return { error };
        }
        throw error;
      }
      toast({
        title: "Verification code sent",
        description: "Check your inbox for a fresh 6-digit code.",
      });
      return { error: null };
    } catch (err) {
      const error = err as Error;
      toast({
        title: "Couldn't resend code",
        description: error.message || "Please try again in a moment.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const redirectUrl = `${window.location.origin}/auth/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        // Handle rate limiting gracefully
        if (error.message.includes('rate limit') || error.message.includes('429')) {
          toast({
            title: "Please Wait",
            description: "You can only request a password reset once per minute. Check your inbox.",
            variant: "destructive",
          });
          return { error };
        }
        
        // Don't reveal if email exists - show generic success message
        console.error('Password reset error:', error);
      }
      
      // Always show success message for security (don't reveal if email exists)
      toast({
        title: "Check Your Email",
        description: "If an account exists with this email, we've sent a password reset link.",
      });
      
      return { error: null };
    } catch (err) {
      const error = err as Error;
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        toast({
          title: "Error Updating Password",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      toast({
        title: "Password Updated",
        description: "Your password has been reset successfully. You can now sign in.",
      });

      return { error: null };
    } catch (err) {
      const error = err as Error;
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
  };

  const value = {
    user,
    session,
    profile,
    loading,
    isAdmin,
    adminRoleLoading,
    signUp,
    signIn,
    signOut,
    refreshProfile,
    resendVerificationEmail,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};